import BaseLoader from '@/main/routes/loaders/base-loader'
import { AccountType } from '@/data/usecases/account/account-type'
import { Account } from '@/domain/usecases'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { UserOnboarding } from '@/domain/usecases/onboarding/user-onboarding'
import { SkillModel } from '@/domain/models/onboarding/user-onboarding-model'
import { LearningGoal } from '@/domain/models/onboarding/learning-goal'
import { WeeklyDedication } from '@/domain/models/onboarding/weekly-dedication'
import { UserProfileAssessment } from '@/domain/usecases/profile-assessment/user-profile-assessment'
import { getWeakness } from '@/domain/models/profile-assessment/user-profile-assessment-model'
import { ContentRecommendedListModel } from '@/domain/models/content/user-content-recommended-model'
import { UserContentRecommended } from '@/domain/usecases/content/user-content-recommended'
import { ContentRecommendationType } from '@/domain/models/content/content-recommendation-type'
import { UserContentSummary } from '@/domain/usecases/content/user-content-summary'
import { UserEnrollmentListItemModel } from '@/domain/models/content/enrollment/user-enrollment-list-model'
import { UserEnrollmentList } from '@/domain/usecases/content/enrollment/user-enrollment-list'

export type RecommendationItem = {
  type: ContentRecommendationType.Enum
  correlationId: number
  contents: ContentRecommendedListModel
}

export type UserSpaceLoaderDataType = {
  profileAssessment: UserProfileAssessment.Model | null
  accountProfessional?: AccountProfessional.Model
  skills: SkillModel[]
  learningGoal: LearningGoal.Enum
  weeklyDedication: WeeklyDedication.Enum
  recommendations: RecommendationItem[]
  enrollments: UserEnrollmentListItemModel[]
}

const MAX_RECOMMENDATION_ITEMS = 3

export default class UserSpaceLoader extends BaseLoader {
  constructor(
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined,
    readonly account: Account.Model
  ) {
    super(account, undefined, queryClient)
  }

  private async getAccountProfessional(): Promise<AccountProfessional.Model | undefined> {
    if (AccountType.Enum.DEPENDENT.is(this.account)) return undefined
    return super.importRemoteModule('makeRemoteAccountProfessional').then(async remote => {
      return (remote(this.queryClient) as AccountProfessional).get(this.account.id)
    })
  }

  private async getOnboarding(): Promise<UserOnboarding.Model> {
    return super.importRemoteModule('makeRemoteUserOnboarding').then(async remote => {
      return (remote(this.queryClient) as UserOnboarding).get()
    })
  }

  private async getAssessment(): Promise<UserProfileAssessment.Model | null> {
    return super.importRemoteModule('makeRemoteUserProfileAssessment').then(async remote => {
      return (remote(this.queryClient) as UserProfileAssessment).getLatest()
    })
  }

  private async getRecommendedContentByAssessment(
    assessment: UserProfileAssessment.Model | null
  ): Promise<RecommendationItem[]> {
    if (!assessment?.competences?.length) {
      return []
    }

    const weakness = getWeakness(assessment)
    const makeRemoteUserContentRecommended = await super.importRemoteModule(
      'makeRemoteUserContentRecommended'
    )
    const remote = makeRemoteUserContentRecommended(this.queryClient) as UserContentRecommended

    return Promise.all(
      weakness.map(item =>
        remote
          .get(
            ContentRecommendationType.Enum.ASSESSMENT_COMPETENCE,
            MAX_RECOMMENDATION_ITEMS,
            item.competence.id
          )
          .then(data => ({
            type: ContentRecommendationType.Enum.ASSESSMENT_COMPETENCE,
            correlationId: item.competence.id,
            contents: data
          }))
      )
    )
  }

  private async getRecommendedContentBySkills(): Promise<RecommendationItem[]> {
    const makeRemoteUserContentSummary = await super.importRemoteModule(
      'makeRemoteUserContentSummary'
    )
    const remote = makeRemoteUserContentSummary(this.queryClient) as UserContentSummary
    return remote.get().then(data =>
      data.skills.map(skill => ({
        type: ContentRecommendationType.Enum.SKILL,
        correlationId: skill.id,
        contents: skill.items.slice(0, MAX_RECOMMENDATION_ITEMS).map((content, i) => ({
          ...content,
          sortOrder: i
        }))
      }))
    )
  }

  private async getRecommendedContent(
    assessment: UserProfileAssessment.Model | null
  ): Promise<RecommendationItem[]> {
    return Promise.all([
      this.getRecommendedContentByAssessment(assessment),
      this.getRecommendedContentBySkills()
    ]).then(data => data.flatMap(x => x))
  }

  private async getUserEnrollments(): Promise<UserEnrollmentListItemModel[]> {
    const makeRemoteUserEnrollmentList = await super.importRemoteModule(
      'makeRemoteUserEnrollmentList'
    )
    const remote = makeRemoteUserEnrollmentList(this.queryClient) as UserEnrollmentList
    return remote.get()
  }

  async loader() {
    return this.deferExec({
      data: Promise.all([
        this.getAssessment(),
        this.getAccountProfessional(),
        this.getOnboarding(),
        this.getUserEnrollments()
      ]).then(async ([profileAssessment, accountProfessional, onboardingModel, enrollments]) => ({
        profileAssessment,
        accountProfessional,
        enrollments,
        skills: onboardingModel.interest.skills,
        learningGoal: onboardingModel.learningGoal,
        weeklyDedication: onboardingModel.weeklyDedication,
        recommendations: await this.getRecommendedContent(profileAssessment)
      }))
    })
  }
}
